import { useEffect, useState } from 'react';
import { Button, Input, Modal, Select, toast } from '../../components/ui';
import { formatMoney } from '../../lib/format';
import { useRecordPayment } from './api';
import { SendReceiptField, receiptClause } from './send-receipt';

/** Payment methods, shared by the modal and the printed/received payment rows. */
export const METHOD_LABELS: Record<string, string> = {
  ach: 'ACH',
  check: 'Check',
  credit_card: 'Credit card',
  wire: 'Wire',
  other: 'Other',
};

/**
 * Most orgs are paid the same way every month, so the method the user picked
 * last time is a far better default than "Not specified" — which is what ends
 * up on the invoice's payment row when nobody bothers to change it.
 */
const LAST_METHOD_KEY = 'solid-financials:last-payment-method';

export function lastUsedMethod(): string {
  try {
    const stored = window.localStorage.getItem(LAST_METHOD_KEY);
    return stored && stored in METHOD_LABELS ? stored : '';
  } catch {
    return '';
  }
}

export function rememberMethod(method: string): void {
  try {
    if (method) window.localStorage.setItem(LAST_METHOD_KEY, method);
  } catch {
    /* private mode — the default just stays unset */
  }
}

/** "7,825.00" — grouped for reading; the field parses commas straight back out. */
function groupAmount(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Accepts "7,825.00", "$7,825", "7825" — anything a human types into money. */
function parseAmount(raw: string): number {
  return Number(raw.replace(/[$,\s]/g, ''));
}

export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

/** Everything the modal needs — a list row satisfies it, so payments can be
 *  recorded without opening the invoice. */
export interface PayableInvoice {
  id: string;
  number: string;
  dueAmount: number;
  client: { name: string };
  /** Payments already recorded — the newest one's method is the best default. */
  payments?: Array<{ date: string; method: string | null }>;
}

/**
 * How this client last paid beats a global preference, which beats "Not
 * specified" — an unlabelled payment row is a reconciliation problem later.
 */
function defaultMethod(invoice: PayableInvoice): string {
  const recorded = [...(invoice.payments ?? [])]
    .reverse()
    .find((p) => p.method && p.method in METHOD_LABELS);
  return recorded?.method ?? lastUsedMethod();
}

export function RecordPaymentModal({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: PayableInvoice;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [amount, setAmount] = useState(() => groupAmount(invoice.dueAmount));
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState(() => defaultMethod(invoice));
  const [notes, setNotes] = useState('');
  // Starts ticked — a client who paid should hear so.
  const [sendReceipt, setSendReceipt] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const record = useRecordPayment();

  // Reset the form each time the modal OPENS (amount defaults to amount due).
  // ⚠ Not when the due amount changes while it is open: recording a payment
  // refreshes the invoice, its due drops to $0.00, and resetting then cleared
  // the mutation — which silently dropped the close and the confirmation, and
  // left the dialog showing "$0.00 due" over a payment that had landed.
  useEffect(() => {
    if (open) {
      setAmount(groupAmount(invoice.dueAmount));
      setDate(todayIso());
      setMethod(defaultMethod(invoice));
      setNotes('');
      setSendReceipt(true);
      setError(null);
      record.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice.id]);

  /**
   * ⚠ EVERY BAD AMOUNT IS NAMED, AND NONE OF THEM READS AS A SETTLEMENT.
   *
   * This form used to validate exactly one state — the over-payment — while
   * `abc`, `-500` and an empty field all fell through to `Number.isFinite`
   * being false, which suppressed the *error* but left the hint underneath
   * still reading "Pays invoice #1156 in full". The one interaction in this app
   * that touches cash was reporting NaN as a full settlement, with the primary
   * button live. Each case now has its own sentence, the hint is shown ONLY
   * when the figure is real, and the button is not pressable until it is.
   */
  const typed = amount.trim();
  const parsed = parseAmount(typed);
  const amountError: string | null =
    typed === ''
      ? 'Enter the amount received.'
      : !Number.isFinite(parsed)
        ? 'That is not an amount — digits only, like 1,234.56.'
        : parsed < 0
          ? 'A payment cannot be negative — remove a mis-entered one instead.'
          : parsed === 0
            ? 'A payment has to be more than $0.00.'
            : parsed > invoice.dueAmount + 0.005
              ? `Exceeds the ${formatMoney(invoice.dueAmount)} due`
              : null;
  // A payment is a thing that has happened. Dating one tomorrow puts money in
  // AR that nobody has received.
  const dateError: string | null = !date
    ? 'Enter the date the payment landed.'
    : date > todayIso()
      ? 'A payment cannot be dated in the future.'
      : null;
  const remaining = amountError ? null : invoice.dueAmount - parsed;
  const blocked = amountError ?? dateError;

  const submit = () => {
    if (blocked) {
      setError(blocked);
      return;
    }
    setError(null);
    // `mutateAsync`, not per-call callbacks: those are dropped if anything
    // resets the mutation first, and the outcome here must always be said.
    void record
      .mutateAsync({
        id: invoice.id,
        amount: Math.round(parsed * 100) / 100,
        date,
        method: method || undefined,
        notes: notes.trim() || undefined,
        sendReceipt,
      })
      .then((updated) => {
        rememberMethod(method);
        onOpenChange(false);
        const said =
          updated.state === 'PAID'
            ? `Invoice #${updated.number} is paid in full`
            : `Payment recorded — ${formatMoney(updated.dueAmount)} still due`;
        const clause = receiptClause(updated.receipt);
        if (updated.receipt && updated.receipt.status !== 'sent' && updated.receipt.status !== 'filed') {
          toast.info(`${said}${clause}`);
        } else {
          toast.success(`${said}${clause}`);
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Payment failed'));
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Record payment"
      description={`Invoice #${invoice.number} · ${invoice.client.name} · ${formatMoney(
        invoice.dueAmount,
      )} due`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={record.isPending}
            disabled={Boolean(blocked)}
            title={blocked ?? undefined}
          >
            Record payment
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Amount"
          // Text, not number: money wants a "$" adornment and thousands
          // separators, and a number input refuses to render either.
          type="text"
          inputMode="decimal"
          autoComplete="off"
          icon={<span className="text-body-sm font-semibold text-grey-500">$</span>}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={() => {
            const value = parseAmount(amount);
            if (Number.isFinite(value) && value > 0) setAmount(groupAmount(value));
          }}
          error={amountError ?? undefined}
          // The hint is a CLAIM about what this figure does, so it may only
          // appear when there is a figure. `remaining` is null whenever the
          // amount is not a real one.
          hint={
            amountError
              ? undefined
              : remaining !== null && remaining > 0.004
                ? `Partial — ${formatMoney(remaining)} would still be due`
                : `Pays invoice #${invoice.number} in full`
          }
          className="num [&_input]:text-right"
        />
        <Input
          label="Date"
          type="date"
          max={todayIso()}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          error={dateError ?? undefined}
        />
        <Select
          label="Method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          options={[
            { value: '', label: 'Not specified' },
            ...Object.entries(METHOD_LABELS).map(([value, label]) => ({ value, label })),
          ]}
        />
        <Input
          label="Notes"
          placeholder="Check #1024, wire ref…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <SendReceiptField className="mt-4" checked={sendReceipt} onChange={setSendReceipt} />
      {error && <p className="mt-3 text-body-sm font-medium text-error">{error}</p>}
    </Modal>
  );
}
